/**
 * Mandatory disclosures shared by /imprint, /privacy and /terms.
 *
 * Every entry is rendered only when it is non-empty. That is deliberate: a visible
 * placeholder such as "[UID EINTRAGEN]" is itself an incorrect mandatory disclosure
 * (UWG Art. 3 Abs. 1 lit. s / DDG § 5) and worse than omitting the line.
 */

export interface LegalContact {
    name: string;
    address: string;
    email: string;
}

export const LEGAL_ENTITY = {
    /**
     * Swiss UID incl. VAT suffix, e.g. 'CHE-123.456.789 MWST'.
     * VAT registration is mandatory from CHF 100'000 annual turnover (MWSTG Art. 10).
     * Leave empty while below the threshold.
     */
    vatNumber: '',

    /**
     * Commercial-register entry, e.g. 'Handelsregisteramt Uri, CHE-123.456.789'.
     * A sole proprietorship must register from CHF 100'000 annual turnover (HRegV Art. 36).
     * Registered 2026-09-04, EHRA-ID 1765934, CH-ID CH-120-1003562-1.
     */
    commercialRegister: 'Handelsregisteramt Uri, CHE-144.248.250',
} as const;

/**
 * Representative in the EU under Art. 27 GDPR.
 *
 * REQUIRED as soon as guests/customers in the EU are targeted: the controller is
 * established in Switzerland (a third country) and Art. 3(2) GDPR applies. The Art. 27(2)
 * exemption does not help here — reservation notes can carry health data (allergies),
 * which is a special category under Art. 9.
 *
 * Fill in name, full postal address and a contact address of the appointed representative.
 */
export const EU_REPRESENTATIVE: LegalContact = {
    name: '',
    address: '',
    email: '',
};

/**
 * Representative in the United Kingdom under Art. 27 UK GDPR.
 * Only required if data subjects in the UK are targeted.
 */
export const UK_REPRESENTATIVE: LegalContact = {
    name: '',
    address: '',
    email: '',
};

export function hasContact(c: LegalContact): boolean {
    return c.name.trim().length > 0;
}
